/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef } from 'react'
import * as yup from 'yup'

import { t } from '@/i18n'
import { ICar } from '@/types'
import { CarCarStatusChoices } from '@/apollo/graphql'
import { useForm } from '@/hooks'
import {
  DatePicker,
  FieldViewLink,
  Form,
  FormButtons,
  OptionSelector,
  TextArea,
  TextInput,
} from '@/components/index'
import CarSubModelSelector from '@/pages/Car/carSubModelSelector'
import { useSelect } from './useSelect'
import { useBankSelect } from './useBankSelect'
import {
  addDriverPath,
  driverDisplayLabel,
  editDealPath,
  editDriverPath,
  generatePath,
  getMaskedString,
} from '@/utils'

interface Props {
  isEditing?: boolean
  initialData?: Partial<ICar>
  loading: boolean
  submitting: boolean
  onCancel: () => void
  onSubmit: (d: ICar) => void
}

const CarForm: React.FC<Props> = ({
  isEditing,
  initialData,
  loading,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const schema: any = useMemo(
    () =>
      yup
        .object({
          creationYear: yup.string().required(t('required_field_error')),
          number: yup
            .string()
            .nullable()
            .min(9, t('invalid_cformat_error'))
            .max(10, t('invalid_cformat_error'))
            .matches(
              /^\(?([0-9]{2})\)?-?([0-9]{3})-?([0-9]{2})$|^\(?([0-9]{3})\)?-?([0-9]{2})-?([0-9]{3})$/,
              t('invalid_cformat_error'),
            )
            .required(t('required_field_error')),
          status: yup
            .mixed<CarCarStatusChoices>()
            .oneOf(
              Object.values(CarCarStatusChoices),
              t('required_field_error'),
            ),
          carSubModel: yup
            .string()
            .nullable()
            .required(t('required_field_error')),
        })
        .required(),
    [],
  )

  const { carStatusOptions, colorOptions, carServiceOptions } = useSelect()

  const {
    loadingBanks,
    bankOptions,
    banksPageInfo,
    onFetchMore: onFetchBanksMore,
  } = useBankSelect({
    loading,
    initialBank: initialData?.bank,
  })

  const { control, isValid, onTriggerChange, onFormSubmit } = useForm<any>({
    isEditing,
    defaultValues: initialData,
    schema,
    onSubmit,
  })

  const formRef = useRef<HTMLFormElement | null>(null)

  const [showDriver, driverLabel] = useMemo(() => {
    const _visible = initialData?.id || !!initialData?.driver
    const label = driverDisplayLabel(initialData?.driver)

    return [_visible, label]
  }, [initialData?.id, initialData?.driver])

  useEffect(() => {
    if (initialData?.number) {
      onTriggerChange('number', getMaskedString(initialData.number, true))
    }
  }, [onTriggerChange, initialData?.number])

  useEffect(() => {
    if (initialData?.bank) {
      onTriggerChange('bank', initialData.bank.id)
    }
  }, [initialData?.bank, onTriggerChange])

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  return (
    <div className="edit-info">
      <div className="data-box">
        {loading ? (
          <div className="loading-text">{t('loading')}</div>
        ) : (
          <>
            <Form<ICar>
              className={isEditing ? 'editing' : 'view'}
              isEditing={isEditing}
              formRef={formRef}
              control={control}
              onSubmit={onFormSubmit}
            >
              <div className="form-fields">
                {showDriver && (
                  <FieldViewLink
                    label={t('driver')}
                    linkLabel={driverLabel || t('add_driver')}
                    link={generatePath(
                      initialData?.driver?.id ? editDriverPath : addDriverPath,
                      {
                        id: initialData?.driver?.id,
                        carId: initialData?.id,
                      },
                    )}
                  />
                )}
                {initialData?.activeDeal && (
                  <FieldViewLink
                    label={t('deal')}
                    linkLabel={`Deal - ${initialData.activeDeal.id}`}
                    link={generatePath(editDealPath, {
                      id: initialData.activeDeal.id,
                    })}
                  />
                )}
                <TextInput
                  required
                  control={control}
                  name="creationYear"
                  type="number"
                  min={0}
                  className="w-50"
                  isEditing={isEditing}
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('creation_year')}
                />
                <TextInput
                  required
                  control={control}
                  isCarNumber
                  name="number"
                  type="text"
                  className="w-50"
                  isEditing={isEditing}
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('number')}
                />
                <TextInput
                  isEditing={isEditing}
                  control={control}
                  className="w-50"
                  name="frameSerialNumber"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('frame_serial_number')}
                />
                <TextInput
                  name="km"
                  isEditing={isEditing}
                  control={control}
                  type="number"
                  className="w-50"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('km_traveled')}
                />
                <DatePicker
                  isEditing={isEditing}
                  className="w-50"
                  control={control}
                  name="licenceExpDate"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('license_exp_date')}
                />
                <TextInput
                  isEditing={isEditing}
                  className="w-50"
                  control={control}
                  name="parkingLocation"
                  label={t('parking_location')}
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                />
                <TextInput
                  isEditing={isEditing}
                  className="w-50"
                  control={control}
                  name="insuranceCompanyName"
                  disabled={!Boolean(isEditing)}
                  emptyValue={emptyValue}
                  label={t('insurance_company_name')}
                />
                <OptionSelector
                  name="serviceProvider"
                  options={carServiceOptions}
                  control={control}
                  menuPlacement="top"
                  className="w-50"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('serviceProvider')}
                />
                <OptionSelector
                  isEditing={isEditing}
                  name="bank"
                  className="w-50"
                  control={control}
                  menuPlacement="top"
                  options={bankOptions}
                  optionsLoading={loadingBanks}
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('bank')}
                  fetchMoreStatus={banksPageInfo.fetchMoreStatus}
                  onFetchMore={() => onFetchBanksMore()}
                />
                <OptionSelector
                  isEditing={isEditing}
                  required
                  className="w-50"
                  control={control}
                  name="status"
                  menuPlacement="top"
                  disabled={!Boolean(isEditing)}
                  emptyValue={emptyValue}
                  options={carStatusOptions}
                  label={t('status')}
                />
                <CarSubModelSelector
                  isFormElement
                  className="w-50"
                  control={control}
                  loading={loading}
                  isEditing={isEditing}
                  initialBrand={initialData?.carSubModel?.carModel?.brand}
                  initialModel={initialData?.carSubModel?.carModel}
                  initialSubModel={initialData?.carSubModel}
                  onTriggerChange={onTriggerChange}
                />
                <OptionSelector
                  isEditing={isEditing}
                  name="color"
                  className="w-50"
                  control={control}
                  menuPlacement="bottom"
                  options={colorOptions}
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('color')}
                />
                <TextArea
                  isEditing={isEditing}
                  className="w-50"
                  control={control}
                  name="notes"
                  label={t('notes')}
                  rows={3}
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                />
                <TextArea
                  isEditing={isEditing}
                  className="w-50"
                  control={control}
                  name="damages"
                  disabled={!Boolean(isEditing)}
                  emptyValue={emptyValue}
                  label={t('damages')}
                  rows={3}
                />
                <DatePicker
                  isEditing={isEditing}
                  className="w-50"
                  control={control}
                  name="insuranceExpDate"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('insurance_exp_date')}
                />
                <DatePicker
                  isEditing={isEditing}
                  className="w-50"
                  control={control}
                  name="lastCheckInTime"
                  showTimeSelect
                  timeIntervals={1}
                  dateFormat="dd/MM/yyyy HH:mm"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('last_check_in_time')}
                />
                <DatePicker
                  isEditing={isEditing}
                  className="w-50"
                  control={control}
                  name="rentalTime"
                  showTimeSelect
                  timeIntervals={1}
                  dateFormat="dd/MM/yyyy HH:mm"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('rental_time')}
                />
                <TextInput
                  control={control}
                  name="salePrice"
                  type="number"
                  min={0}
                  className="w-50"
                  isEditing={isEditing}
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('salePrice')}
                />
                <DatePicker
                  isEditing={isEditing}
                  className="w-50"
                  control={control}
                  name="saleTime"
                  showTimeSelect
                  timeIntervals={1}
                  dateFormat="dd/MM/yyyy HH:mm"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('sale_time')}
                />
                <TextInput
                  control={control}
                  className="w-50"
                  isEditing={isEditing}
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  name="vehicleCode"
                  label={t('vehicleCode')}
                />
              </div>
            </Form>

            <FormButtons
              visible={isEditing}
              formRef={formRef}
              isFormValid={isValid}
              submitting={submitting}
              onCancel={onCancel}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default CarForm
